import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from 'hooks/useUser';
import DashboardLayout from 'layouts/DashboardLayout';
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { format } from 'date-fns';

// New components
import MetricSelector from './components/MetricSelector';
import DateRangeSelector from './components/DateRangeSelector';
import AggregationControls from './components/AggregationControls';
import ResultsTable from './components/ResultsTable';
import ExportButton from './components/ExportButton';
import AuditTab from './components/AuditTab';

// Hooks and services
import { useMetricData } from './hooks/useMetricData';
import { useAggregation } from './hooks/useAggregation';

// Constants
import { DEFAULT_SELECTED_METRICS, AUTHORIZED_USERS, AUTHORIZED_DISPLAY_NAMES } from './constants';

// Utils
import { validateQueryInputs } from './utils/validators';

export default function MetricDataDevTool() {
  const { user } = useUser();

  // State
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedMetrics, setSelectedMetrics] = useState(DEFAULT_SELECTED_METRICS);
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [loadingStores, setLoadingStores] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [currentTab, setCurrentTab] = useState(0); // 0 = Query, 1 = Audit

  // Access control
  const displayName = user?.firstName ? `${user.firstName} ${user.lastName}` : user?.userName;
  const isAuthorized =
    AUTHORIZED_USERS.includes(user?.userName) ||
    AUTHORIZED_DISPLAY_NAMES.includes(displayName);

  // Custom hooks
  const {
    allData,
    loading,
    error,
    progress,
    fetchData,
    cancelQuery,
    reset
  } = useMetricData(user?.client);

  // Filter data by selected metrics (client-side)
  const filteredData = useMemo(() => {
    if (!allData || allData.length === 0) {
      return [];
    }
    return allData.filter(item => selectedMetrics.includes(item.metricName));
  }, [allData, selectedMetrics]);

  const {
    aggregationConfig,
    aggregatedData,
    description: aggregationDescription,
    updateConfig: setAggregationConfig
  } = useAggregation(filteredData);

  // Fetch stores on mount
  useEffect(() => {
    if (isAuthorized) {
      fetchStores();
    }
  }, [isAuthorized]);

  const fetchStores = async () => {
    setLoadingStores(true);
    try {
      const { API, graphqlOperation } = await import('aws-amplify');
      const { listStoreRecords } = await import('graphql/queries');

      const response = await API.graphql(graphqlOperation(listStoreRecords));
      const storeItems = response.data.listStoreRecords.items || [];
      const sortedStores = storeItems.sort((a, b) =>
        (a.storeName || '').localeCompare(b.storeName || '')
      );
      setStores(sortedStores);
    } catch (err) {
      console.error('Error fetching stores:', err);
      setValidationError('Failed to load stores. Please try again.');
    } finally {
      setLoadingStores(false);
    }
  };

  const handleFetchMetricData = async () => {
    console.log('🚀 [MetricData Tool] Fetch initiated with:', {
      selectedStore,
      dateRange,
      selectedMetrics,
      selectedMetricsCount: selectedMetrics.length,
      aggregationConfig,
      userClient: user?.client
    });

    // Validate inputs
    const validation = validateQueryInputs({
      storeId: selectedStore,
      startDate: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : null,
      endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : null,
      selectedMetrics,
      aggregationConfig
    });

    console.log('✅ [MetricData Tool] Validation result:', validation);

    if (!validation.valid) {
      setValidationError(validation.errors.join('. '));
      return;
    }

    setValidationError(null);

    const queryParams = {
      storeId: selectedStore,
      startDate: format(dateRange.from, 'yyyy-MM-dd'),
      endDate: format(dateRange.to, 'yyyy-MM-dd')
      // Note: selectedMetrics NOT sent to query - all metrics fetched, filtering happens client-side
    };

    console.log('📡 [MetricData Tool] Calling fetchData with:', queryParams);
    console.log('📊 [MetricData Tool] Client-side filter will apply for metrics:', selectedMetrics);

    await fetchData(queryParams);
  };

  const handleReset = () => {
    setSelectedStore('');
    setSelectedMetrics(DEFAULT_SELECTED_METRICS);
    setDateRange({ from: null, to: null });
    setAggregationConfig({ byDate: false, byDayPart: false, byDiningOption: false });
    setValidationError(null);
    reset();
  };

  // Access denied
  if (!isAuthorized) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Typography variant="h6">Access Denied</Typography>
            <Typography>
              This tool is only available to Dev User and Radix Admin accounts.
            </Typography>
          </Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          MetricData Dev Tool
        </Typography>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Query" />
            <Tab label="Audit" />
          </Tabs>
        </Box>

        {/* Query Tab Content */}
        {currentTab === 0 && (
          <>
            {/* Filters Card */}
            <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              {/* Store Selection */}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="store-select-label" sx={{ color: '#7C3AED' }}>Select Store</InputLabel>
                  <Select
                    labelId="store-select-label"
                    value={selectedStore}
                    label="Select Store"
                    onChange={(e) => setSelectedStore(e.target.value)}
                    disabled={loadingStores || loading}
                    sx={{
                      height: '40px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#7C3AED',
                        borderWidth: '2px'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6D28D9'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#7C3AED'
                      }
                    }}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.storeName || `Store ${store.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Metric Selection */}
              <Grid item xs={12} md={3}>
                <MetricSelector
                  selectedMetrics={selectedMetrics}
                  onChange={setSelectedMetrics}
                  disabled={loading}
                />
              </Grid>

              {/* Date Range Selection */}
              <Grid item xs={12} md={3}>
                <DateRangeSelector
                  dateRange={dateRange}
                  onChange={setDateRange}
                  disabled={loading}
                />
              </Grid>

              {/* Aggregation Controls */}
              <Grid item xs={12} md={3}>
                <AggregationControls
                  aggregationConfig={aggregationConfig}
                  onChange={setAggregationConfig}
                  disabled={loading}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <AdminButton
                    variant="contained"
                    onClick={handleFetchMetricData}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Fetch MetricData'}
                  </AdminButton>
                  <AdminButton
                    variant="outlined"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Reset
                  </AdminButton>
                  {allData.length > 0 && (
                    <ExportButton
                      data={aggregatedData}
                      selectedMetrics={selectedMetrics}
                      dateRange={dateRange}
                      aggregationConfig={aggregationConfig}
                      disabled={loading}
                      onError={(msg) => setValidationError(msg)}
                    />
                  )}
                  {loading && progress.hasMore && (
                    <Typography variant="body2" color="text.secondary">
                      Loading... {progress.count} records (page {progress.page})
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>

            {/* Error Display */}
            {(validationError || error) && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="error">{validationError || error}</Alert>
              </Box>
            )}
          </CardContent>
        </Card>

            {/* Results Card */}
            {(allData.length > 0 || loading) && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                      MetricData Results
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {aggregationDescription} - {aggregatedData.length} rows
                      {allData.length > 0 && ` (${allData.length} total records, ${selectedMetrics.length} metrics selected)`}
                    </Typography>
                  </Box>
                  <ResultsTable
                    data={aggregatedData}
                    aggregationConfig={aggregationConfig}
                    loading={loading}
                    emptyMessage="No data found for the selected criteria"
                  />
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Audit Tab Content */}
        {currentTab === 1 && (
          <AuditTab />
        )}
      </Box>
    </DashboardLayout>
  );
}
